<template>
  <div class="min-h-screen transition-colors duration-300 font-sans text-left"
    :class="isDark ? 'bg-black text-zinc-300' : 'bg-[#FDFCF8] text-[#7A7367]'">
    
    <header class="relative border-b py-16 md:py-24 overflow-hidden select-none"
      :class="isDark ? 'border-zinc-900 bg-zinc-950/40' : 'border-[#E5E0D8] bg-[#F7F5F0]/40'">
      <div class="max-w-7xl mx-auto px-6 relative z-10 space-y-4">
        <div class="text-[10px] font-bold uppercase tracking-[0.25em]" :class="isDark ? 'text-zinc-500' : 'text-[#A69B88]'">
          Prestation & Localisation Élite
        </div>
        <h1 class="text-3xl md:text-5xl font-semibold tracking-tight max-w-3xl leading-none" :class="isDark ? 'text-white' : 'text-[#2D2924]'">
          {{ activityDisplay }} à <span class="italic font-serif font-light text-[#A69B88]">{{ cityName || citySlug }}</span>
        </h1>
        <p class="text-xs md:text-sm font-light max-w-xl leading-relaxed font-mono opacity-80">
          {{ totalSalons }} {{ activityDisplay.toLowerCase() }}{{ totalSalons > 1 ? 's' : '' }} d'exception disponible{{ totalSalons > 1 ? 's' : '' }} pour une réservation immédiate.
        </p>
      </div>
    </header>

    <main class="max-w-7xl mx-auto px-6 py-12 md:py-16 space-y-12">
      
      <section class="rounded-2xl border p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-all"
        :class="isDark ? 'bg-zinc-950/20 border-zinc-900' : 'bg-white border-[#E5E0D8] shadow-sm shadow-zinc-950/5'">
        
        <div class="lg:col-span-7 space-y-4">
          <h2 class="text-base font-semibold tracking-tight" :class="isDark ? 'text-white' : 'text-[#2D2924]'">
            Trouvez votre {{ activityDisplay.toLowerCase() }} à {{ cityName || citySlug }}
          </h2>
          <p class="text-xs font-light leading-relaxed" :class="isDark ? 'text-zinc-400' : 'text-[#8C8579]'">
            Comparez les artisans les plus renommés et les adresses confidentielles de {{ cityName || citySlug }}.
            Consultez les notes certifiées, parcourez les grilles de tarifs de leurs prestations et réservez instantanément votre créneau sur BookMySalon.
          </p>

          <div class="space-y-3 pt-2">
            <h3 class="text-[10px] font-bold uppercase tracking-wider" :class="isDark ? 'text-zinc-500' : 'text-[#A69B88]'">
              Explorer d'autres rituels à {{ cityName }}
            </h3>
            <div class="flex flex-wrap gap-2">
              <router-link
                v-for="activity in otherActivities"
                :key="activity.slug"
                :to="`/reserver/${activity.slug}/${citySlug}`"
                class="px-3.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-200 border"
                :class="isDark 
                  ? 'bg-zinc-900/50 border-zinc-800/80 text-zinc-400 hover:text-white hover:bg-zinc-850' 
                  : 'bg-[#F7F5F0] border-[#E5E0D8]/60 text-[#7A7367] hover:border-[#A69B88] hover:text-[#2D2924]'"
              >
                {{ activity.name }}
              </router-link>
            </div>
          </div>
        </div>

        <div class="lg:col-span-5 border-l pl-6 lg:pl-8 space-y-4 font-light text-xs"
          :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
          <h4 class="text-[10px] font-bold uppercase tracking-wider" :class="isDark ? 'text-zinc-500' : 'text-[#A69B88]'">
            L'expérience BookMySalon
          </h4>
          <ul class="space-y-3 p-0 m-0 list-none">
            <li v-for="benefit in benefits" :key="benefit" class="flex items-center gap-2.5">
              <svg class="w-4 h-4 opacity-70 shrink-0" :class="isDark ? 'text-zinc-400' : 'text-[#2D2924]'" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{{ benefit }}</span>
            </li>
          </ul>
        </div>
      </section>

      <div v-if="loading" class="flex flex-col items-center justify-center py-20 space-y-4">
        <div class="w-6 h-6 border-2 rounded-full animate-spin border-transparent"
          :class="isDark ? 'border-t-white' : 'border-t-[#2D2924]'"></div>
        <p class="text-[11px] font-mono uppercase tracking-widest opacity-60">Filtrage des adresses...</p>
      </div>

      <div v-else>
        <div v-if="salons.length === 0" class="text-center py-20 border border-dashed rounded-2xl space-y-5"
          :class="isDark ? 'border-zinc-900 text-zinc-600' : 'border-[#E5E0D8] text-[#8C8579]'">
          <div class="space-y-2">
            <svg class="w-6 h-6 mx-auto opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-xs font-light">Aucun spécialiste d'activité "{{ activityDisplay.toLowerCase() }}" n'est enregistré pour le moment à {{ cityName }}.</p>
          </div>
          <router-link
            :to="`/villes/${citySlug}`"
            class="inline-flex px-5 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all border"
            :class="isDark 
              ? 'bg-white text-black border-transparent hover:bg-[#D5CCBC]' 
              : 'bg-[#2D2924] text-white border-transparent hover:bg-[#A69B88]'"
          >
            Découvrir tous les salons de la ville
          </router-link>
        </div>

        <div v-else class="space-y-6">
          <div class="border-b pb-3" :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
            <h2 class="text-xs font-mono uppercase tracking-widest opacity-70">
              {{ totalSalons }} Établissement{{ totalSalons > 1 ? 's' : '' }} Référencé{{ totalSalons > 1 ? 's' : '' }}
            </h2>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SalonCard
              v-for="salon in salons"
              :key="salon._id || salon.id"
              :salon="salon"
              class="transition-transform duration-300 hover:scale-[1.01]"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useTheme } from '@/composables/useTheme'; // Récupération de ton système de thème personnalisé
import axios from 'axios';
import { useMeta } from '@/composables/useMeta';
import SalonCard from '@/components/SalonCard.vue';

const ACTIVITY_NAMES = {
  'coiffure': 'Coiffeur',
  'beaute': 'Salon de beauté',
  'barbier': 'Barbier',
  'spa': 'Spa',
  'esthetique': 'Esthéticienne',
  'onglerie': 'Salon de manucure'
};

export default {
  name: 'ActivityCityView',
  components: { SalonCard },
  setup() {
    const route = useRoute();
    const { isDark } = useTheme(); // Initialisation de ton composable de thème

    const activitySlug = computed(() => route.params.activity);
    const citySlug = computed(() => route.params.city);
    const cityName = ref('');
    const activityDisplay = computed(() => ACTIVITY_NAMES[activitySlug.value] || activitySlug.value);
    const salons = ref([]);
    const totalSalons = ref(0);
    const loading = ref(true);

    const benefits = [
      'Réservation instantanée sécurisée 24h/24',
      'Confirmation de créneau immédiate par SMS/Mail',
      'Avis clients certifiés suite à un passage réel',
      'Garantie des meilleurs tarifs de l\'établissement'
    ];

    const otherActivities = computed(() => {
      return Object.entries(ACTIVITY_NAMES)
        .filter(([slug]) => slug !== activitySlug.value)
        .map(([slug, name]) => ({ slug, name }));
    });

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://bookmysalon-967a856b16b6.herokuapp.com/api/seo/activity/${activitySlug.value}/${citySlug.value}`
        );

        cityName.value = response.data.city || citySlug.value;
        salons.value = [...(response.data.firms || []), ...(response.data.prospects || [])];
        totalSalons.value = response.data.total || 0;

        useMeta({
          title: `${activityDisplay.value} à ${cityName.value} — Réservation en ligne | BookMySalon`,
          description: `Trouvez et réservez un rendez-vous parmi les ${totalSalons.value} meilleurs ${activityDisplay.value.toLowerCase()}s à ${cityName.value}. Avis authentiques, tarifs et disponibilités instantanées.`,
          canonical: `https://bookmysalon.com/reserver/${activitySlug.value}/${citySlug.value}`,
          ogTitle: `Meilleurs ${activityDisplay.value}s à ${cityName.value} — Sélection Luxe`,
          ogDescription: `Accédez aux agendas en ligne des professionnels de l'activité ${activityDisplay.value.toLowerCase()} à ${cityName.value}.`,
          ogImage: salons.value[0]?.logo || salons.value[0]?.image || 'https://bookmysalon.com/default-og-image.jpg'
        });

        loading.value = false;
      } catch (error) {
        console.error('Erreur lors du traitement sémantique Activité-Ville :', error);
        loading.value = false;
      }
    };

    onMounted(fetchData);

    return {
      isDark,
      activitySlug,
      citySlug,
      cityName,
      activityDisplay,
      salons,
      totalSalons,
      loading,
      otherActivities,
      benefits
    };
  }
};
</script>